import React from 'react';
import { Container, TableContainer, Paper, Table, TableBody, TableRow, TableCell, Typography, FormControl, Select, MenuItem, useMediaQuery, useTheme } from '@mui/material';



// PC用のカレンダーコンポーネント
function DesktopCalendar({ daysInWeeks, weeks, shiftPattern, selectedValue, handleChangeForShift }) {
  return (
    <TableBody>
      <TableRow>
        {weeks.map((weekDay, idx) => (
          <TableCell
            align="center"
            key={weekDay}
            sx={{
              border: 1,
              borderColor: 'grey.300',
              color: idx === 0 ? '#F44336' : idx === 6 ? '#3F51B5' : 'inherit',
            }}
          >
            {weekDay}
          </TableCell>
        ))}
      </TableRow>
      {daysInWeeks.map((week, i) => (
        <TableRow key={i}>
          {week.map((day, index) => (
            <TableCell key={index} align="center" sx={{ border: 1, borderColor: 'grey.300', position: 'relative', pt: 0.5 }}>
              {day !== null ? (
                <>
                  <Typography component="div" sx={{ borderBottom: 1, borderColor: 'grey.300', width: '100%', fontSize: "14px" }}>
                    {day}
                  </Typography>
                  <FormControl fullWidth sx={{ m: 0 }}>
                    <Select
                      value={selectedValue[day - 1]}
                      onChange={(e) => handleChangeForShift(day - 1, e)}
                      displayEmpty
                      inputProps={{ 'aria-label': 'Without label' }}
                      sx={{
                        fontSize: '12px',
                        '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                        '& .MuiSelect-select': { py: 0.5, pr: 1, pl: 1, appearance: 'none' },
                        '&:before, &:after, & .MuiOutlinedInput-notchedOutline': { display: 'none' },
                        backgroundColor: 'transparent',
                        '& .MuiSelect-icon': {
                          color: 'action.active',
                          top: 'calc(50% - 12px)',
                        }
                      }}
                    >
                      {shiftPattern && Object.keys(shiftPattern).map((key) => 
                        <MenuItem
                          key={key}
                          value={key}
                        >
                            {shiftPattern[key]}
                        </MenuItem>
                      )}
                      <MenuItem value="x">x</MenuItem>
                      <MenuItem value="pto">有給予定</MenuItem>
                    </Select>
                  </FormControl>
                </>
              ) : null}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
}



// モバイル用のカレンダーコンポーネント
function MobileCalendar({ days, weeks, shiftPattern, selectedValue, handleChangeForShift }) {

  return (
    <TableBody>
      {days.map((day, index) => (
          (day !== null) && (
            <TableRow key={index} sx={{ height: '24px' }}>
              <TableCell sx={{ py: 0.5 }}>
                {day}(
                  <Typography 
                    component="span"
                    sx={{
                      fontSize: '12px',
                      color: index % 7 === 0 ? '#F44336' : index % 7 === 6 ? '#3F51B5' : 'inherit'
                    }}
                  >
                    {weeks[index % 7]}
                  </Typography>
                  
                )
                <FormControl fullWidth>
                <Select
                    value={selectedValue[day - 1]}
                    onChange={(e) => handleChangeForShift(day - 1, e)}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Without label' }}
                    sx={{
                        textAlign: 'center',
                        width: '60%',
                        height: '24px',
                        fontSize: '14px',
                        '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                        '& .MuiSelect-select': { py: 0, pr: 1, pl: 1, appearance: 'none' },
                        '&:before, &:after, & .MuiOutlinedInput-notchedOutline': { display: 'none' },
                        backgroundColor: 'transparent',
                        '& .MuiSelect-icon': {
                            color: 'action.active',
                            top: 'calc(50% - 12px)',
                        }
                    }}
                >
                    {shiftPattern && Object.keys(shiftPattern).map((key) => 
                      <MenuItem
                        key={key}
                        value={key}
                      >
                          {shiftPattern[key]}
                      </MenuItem>
                    )}
                    <MenuItem value="x">x</MenuItem>
                    <MenuItem value="pto">有給予定</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
            </TableRow>
          ) 
      ))}
    </TableBody>
  );
}



function Main({year, month, employeeCodeIsValid, hasRegisteredShifts, pastDeadLine, shiftPattern, selectedShiftValues, handleChangeForShift}) {

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const firstDay = new Date(year, month-1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();

  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const daysInWeeks = [];

  // 初日の前の空白を挿入
  const startIndex = firstDay;
  for (let i = 0; i < startIndex; i++) {
    days.unshift(null);
  }

  // 週ごとに分割
  for (let i = 0; i < days.length; i += 7) {
    daysInWeeks.push(days.slice(i, i + 7));
  }

  const weeks = ["日", "月", "火", "水", "木", "金", "土"];

  
  return (
    <Container sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <TableContainer 
        component={Paper} 
        sx={{ 
          maxWidth: { 
            xs: "100%", 
            lg: "65%",

          }, 
          overflow: 'hidden', 
          background: 'white', 
          border: '1px solid #ccc', 
          borderRadius: '4px' 
        }}
      >
        { pastDeadLine && (
          <Typography 
            variant="h6" 
            sx={{ 
              textAlign: 'center', 
              color: 'red', 
              fontWeight: 'bold', 
              padding: '10px 0' 
            }}
          >
            翌月分シフト登録期日を過ぎています
          </Typography>
        )}
        { selectedShiftValues.every(elm => [...Object.keys(shiftPattern), "x", "pto"].includes(elm))  &&
          employeeCodeIsValid && hasRegisteredShifts && (
            <Table 
              sx={{ 
                minWidth: 500, 
                maxWidth: '100%', 
                borderCollapse: 'collapse', 
                tableLayout: 'fixed' 
                }}
            >
              {isMobile ? 
                <MobileCalendar
                  days={days} 
                  weeks={weeks}
                  shiftPattern={shiftPattern}
                  selectedValue={selectedShiftValues} 
                  handleChangeForShift={handleChangeForShift} 
                /> : 
                <DesktopCalendar 
                  daysInWeeks={daysInWeeks} 
                  weeks={weeks}
                  shiftPattern={shiftPattern}
                  selectedValue={selectedShiftValues} 
                  handleChangeForShift={handleChangeForShift} 
                />
              }
            </Table>
          )
        }
      </TableContainer>
    </Container>
  );
}

export default Main;

